<template>
  <div class="map-container">

    <base-map
      :map-center="map.center"
      :map-zoom="map.zoom"
      :vessels="vessels"
      :selected-vessel-imo="selectedVesselIMO"
      :highlighted-vessel-imo="highlightedVesselIMO"
      @vessel-popup-ready="handleVesselPopupReady"
      @map-update="handleMapMove">

      <template #control-top-right>
        <div v-if="vesselsCount" class="vessel-count map-overlay">
          Total vessels count: {{ vesselsCount }}
        </div>
      </template>
    </base-map>

  </div>
</template>

<script>
  import debounce from 'lodash-es/debounce';
  import getNested from 'lodash-es/get';
  import isEqual from 'lodash-es/isEqual';

  import { mapActions } from 'vuex';
  import BaseMap from '@/base_components/BaseMap';


  const debounceDelay = 300;

  export default {
    name: 'vessels-map',

    props: {
      defaultZoom: {
        type: Number,
        default: null,
      },

      vesselsCount: {
        type: Number,
        default: null,
      },

      filterValues: {
        type: Object,
        default: () => ({}),
      },

      requiredFilterFields: {
        type: Array,
        default: () => [
          // 'boundingBox', // auto added in computed
          'segment',
          'ladenStatus',
          'commodity',
          'loadCountry',
          'dischargeCountry',
        ]
      },

      modalShow: {
        // when map is used inside a modal,
        // "modalShow" contains is it displayed or not
        type: Boolean,
        default: false
      },
    },

    components: {
      BaseMap,
    },

    data() {
      return {
        apiFilterValues: {},
        vessels: [],
        vesselsLimitCount: 50,
        selectedVesselIMO: null,
        highlightedVesselIMO: null,

        map: {
          instance: null,
          zoom: this.defaultZoom || 2,
          center: [47.41322, -1.219482],
        },
      };
    },

    methods: {
      ...mapActions('api/livestates', ['getVesselsInArea']),

      async handleMapMove(data) {
        const coordinates = {...data, limit: this.vesselsLimitCount};
        this.$emit('map-update', coordinates);

        this.apiFilterValues = {
          ...this.filterValues,
          boundingBox: coordinates,
        };
      },

      loadVesselsInAreaHandler: debounce(function(apiData) {
          this.loadVesselsInArea(apiData);
        },
        debounceDelay,
      ),

      async loadVesselsInArea(apiData) {
        // console.log('loadVesselsInArea', JSON.stringify(apiData, null, 2));
        let response = await this.getVesselsInArea(apiData);

        this.vessels = getNested(response, 'vessels', []).map(vessel => {
          return {
            ...vessel,
            geometry: {
              coordinates: getNested(JSON.parse(vessel.geomCoordinates), 'coordinates', [0, 0]),
            },
            position: {
              speed: getNested(vessel, 'speed', 0),
              course: getNested(vessel, 'course', 0),
            },
            state: {
              ladenStatus: vessel.ladenStatus === 'L' ? 'Laden' : 'Ballast',
            },
            popupData: {
              'IMO': vessel.imo,
              'Name': vessel.vesselName,
            },
          }
        });
      },

      handleVesselPopupReady(evt) {
        evt._source.on('popupopen', this.handlePopupOpen);
        evt._source.on('popupclose', this.handlePopupClose);
      },

      handlePopupOpen(evt) {
        this.selectedVesselIMO = getNested(evt, 'popup.options.itemId', null);
        this.highlightedVesselIMO = getNested(evt, 'popup.options.itemId', null);
      },

      handlePopupClose() {
        this.selectedVesselIMO = null;
        this.highlightedVesselIMO = null;
      },

      isFilterReady() {
        return isEqual(
          Object.keys(this.apiFilterValues).sort(),
          this.requiredFieldsComputed.sort()
        );
      },
    },

    computed: {
      requiredFieldsComputed() {
        return [
          ...this.requiredFilterFields,
          'boundingBox',
        ];
      },
    },

    watch: {
      filterValues(parameters) {
        this.apiFilterValues = {
          ...this.apiFilterValues,
          ...parameters
        };
      },

      apiFilterValues(apiFilterValues) {
        if(this.isFilterReady()) {
          this.loadVesselsInAreaHandler(apiFilterValues);
        }
      },

      modalShow(value) {
        if(value && this.map.instance) {
          this.map.instance.invalidateSize();
        }
      },
    },
  };
</script>

<style scoped>
  .map-container >>> .vue2leaflet-map {
    position: absolute;
    top: 0;
    left: 0;
  }

  .map-container >>> .vessel-highlight {
    z-index: 2000 !important;
  }

  .map-container >>> .vessel-icon {
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    /*background-color: rgba(255, 255, 0, 0.25);*/
  }

  .map-container >>> .vessel-icon-arrow {
    width: 25px;
    height: 25px;
  }

  .map-container >>> .vessel-icon-circle {
    width: 10px;
    height: 10px;
  }
</style>

<style scoped lang="scss">
  .map-container {
    padding: 0;
    position: relative;
    min-height: 370px;

    .vessel-count {
      padding: 0.25rem 0.5rem;
      border-radius: 3px;
    }
  }
</style>
